import { createFileRoute, Link } from '@tanstack/react-router'
import { FeaturePage } from '@/components/feature-page'
import { ProductFrame } from '@/components/marketing/product-frame'
import { ChatAlertPiece } from '@/components/marketing/ui-pieces'
import { breadcrumbList, jsonLdScript, webPage } from '@/lib/schema'

export const Route = createFileRoute('/_site/features/browser-monitoring')({
  component: BrowserMonitoringPage,
  head: () => ({
    meta: [
      { title: 'Browser Monitoring — Synthetic Playwright Checks | Uptrack' },
      { name: 'description', content: 'Run real browser flows on schedule with Playwright. Per-step screenshots, visual diffing, HAR/console capture. Pro from $3/mo.' },
      { property: 'og:title', content: 'Browser Monitoring — Synthetic Playwright Checks | Uptrack' },
      { property: 'og:description', content: 'Schedule real browser flows with Playwright. Per-step screenshots, visual regression, and HAR/console/DOM on failure.' },
      { property: 'og:image', content: 'https://uptrack.app/og/features-browser-monitoring.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/features/browser-monitoring' }],
    scripts: [
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Features', url: 'https://uptrack.app/features' },
        { name: 'Browser Monitoring', url: 'https://uptrack.app/features/browser-monitoring' },
      ])),
      jsonLdScript(webPage({
        name: 'Uptrack Browser Monitoring',
        description: 'Synthetic browser checks that run Playwright scripts on a schedule with per-step screenshots and visual regression.',
        url: 'https://uptrack.app/features/browser-monitoring',
      })),
    ],
  }),
})

const tiers = [
  { plan: 'Pro', browser: '5 browser monitors', frequency: 'Every 10 min', highlight: true },
  { plan: 'Team', browser: '20 browser monitors', frequency: 'Every 5 min', highlight: false },
  { plan: 'Business', browser: 'Unlimited', frequency: 'Every 1 min', highlight: false },
]

function BrowserMonitoringPage() {
  return (
    <FeaturePage
      section="Browser monitoring"
      title="Check the flow, not just the home page."
      intro="A ping tells you the server answered. A browser check tells you a real user could log in, add to cart, and check out. Write a Playwright script; Uptrack runs it on a schedule and shows you a screenshot of every step."
      featuresHeading="What each run gives you"
      features={[
        { title: 'Real Playwright scripts', desc: 'Bring the scripts you already write. page, expect, and a step() helper are in scope — no new DSL to learn.' },
        { title: 'A screenshot per step', desc: 'Every step is captured, so when a flow breaks you see exactly where — not just that it failed.' },
        { title: 'Visual regression', desc: 'Each run is compared to a baseline. A layout shift or missing element drops the match score and alerts you, with a before/after view.' },
        { title: 'Console, network, DOM', desc: 'On failure, the run keeps the HAR, console log, and DOM snapshot so you can debug without reproducing it locally.' },
      ]}
      table={{
        heading: 'Browser checks by plan',
        intro: 'Every paid plan includes browser checks, with capacity and frequency increasing by plan.',
        columns: ['Plan', 'Browser monitors', 'Check frequency'],
        rows: tiers.map((t) => ({ cells: [t.plan, t.browser, t.frequency], highlight: t.highlight })),
      }}
      cta={{ heading: 'Start monitoring today', body: 'Choose a paid plan for scheduled browser checks and uptime monitoring.' }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <ChatAlertPiece />
        </ProductFrame>
      }
    >
      <section className="border-t border-border">
        <div className="container mx-auto px-4 py-10">
          <p className="max-w-2xl text-lg leading-[1.7] text-body">
            New to scripted checks? Read{' '}
            <Link to="/blog/synthetic-monitoring-services-guide" className="text-primary hover:underline">
              synthetic monitoring services: how to choose one
            </Link>{' '}
            for the four kinds of check, when an uptime check is enough, and what to ask a service.
          </p>
        </div>
      </section>
    </FeaturePage>
  )
}
